import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useBooking } from '../contexts/BookingContext';
import { aFechaISO } from '../lib/calendario';
import './BookingCalendar.css';
import { API_URL } from '../config/api';


const BookingCalendar = ({ businessInfo, services, selectedStaff, selectedResource, onSelectSlot, refreshTrigger, isRescheduling = false, excludeDateTime = null, currentBooking = null, alMostrarHorarios = null }) => {
  /**
   * El día elegido. Arranca en `null`: NADA preseleccionado.
   *
   * Antes arrancaba en `new Date()`, o sea hoy. Eso pinta un día como elegido
   * sin que nadie lo haya tocado y muestra sus horarios abajo, así que la
   * persona ve una lista de turnos y no registra que todavía no eligió el día:
   * elige la hora creyendo que el día ya estaba resuelto. Cuando hoy no es el
   * día que quería —que es casi siempre, porque la mayoría reserva para más
   * adelante—, el error recién se nota en la confirmación.
   *
   * `null` obliga a que el primer gesto del calendario sea elegir el día, que
   * es el orden real de la decisión: primero cuándo, después a qué hora.
   */
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedService, setSelectedService] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth());
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [dayBookings, setDayBookings] = useState([]); // Reservas del día seleccionado
  const [loadingSlots, setLoadingSlots] = useState(false); // Loading de slots
  const [blockedSlots, setBlockedSlots] = useState([]); // Horarios bloqueados
  /**
   * Cuántos turnos hay tomados cada día del mes que se está viendo:
   * `{ '2026-08-12': 3, ... }`.
   *
   * Sin esto el puntito de disponibilidad no puede significar nada. El cálculo
   * de abajo sólo restaba los bloqueos manuales, así que cualquier día abierto
   * daba 100% y salía verde aunque estuviera lleno: el amarillo y el rojo eran
   * inalcanzables.
   *
   * Viene en conteos y no en turnos a propósito — ver el endpoint. */
  const [ocupacionDelMes, setOcupacionDelMes] = useState({});
  /**
   * Los horarios que el SERVIDOR dice que están tomados, para el servicio, el
   * profesional y el lugar elegidos.
   *
   * Hasta acá esto lo decidía este componente solo, y no podía: los datos que
   * le llegan de `by-date` son `{ time, status }` y nada más —a esa ruta le
   * sacaron `service` y `staff` porque es pública y filtraba la agenda de
   * clientes del negocio—. Así que `booking.staff?.id` era siempre `undefined`
   * y ningún profesional figuraba nunca ocupado: la página ofrecía un horario
   * con el profesional ya reservado y el turno recién se rechazaba al enviar el
   * formulario, con todo completado.
   *
   * Ahora lo contesta el backend con el mismo `decidirConflicto` que usa la
   * creación del turno, así que lo que se ofrece es lo que se puede reservar.
   */
  const [horariosOcupados, setHorariosOcupados] = useState(() => new Set());
  const [consultandoOcupados, setConsultandoOcupados] = useState(false);
  /**
   * Un contador que sube cuando hay que volver a preguntar quién está ocupado.
   *
   * La disponibilidad se consultaba una sola vez, al elegir día y profesional, y
   * después quedaba congelada. Pero el horario libre que estás mirando no es
   * tuyo hasta que reservás: mientras completás el formulario, otro lo puede
   * estar tomando. Con la lista vieja en pantalla, esa persona llega hasta el
   * final y recién ahí se come el rechazo — que es justo la experiencia que
   * estamos sacando.
   *
   * Se revalida al volver a la pestaña y cada tanto mientras hay horarios a la
   * vista. Ver el efecto de más abajo.
   */
  const [revalidacion, setRevalidacion] = useState(0);
  const { isSlotOcupado } = useBooking();
  


  // Función para obtener todas las reservas de un día específico
  const fetchDayBookings = useCallback(async (fecha) => {
    try {
      const response = await fetch(`${API_URL}/api/bookings/by-date?businessId=${businessInfo?.id}&date=${fecha}`);
      
      if (response.ok) {
        const data = await response.json();
        return data.bookings;
      } else {

        return [];
      }
      
    } catch (error) {
      // Backend no disponible, usar contexto local
      return [];
    }
  }, [businessInfo?.id]);

  // Función para obtener horarios bloqueados del negocio
  const fetchBlockedSlots = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/blocked-slots?businessId=${businessInfo?.id}`);
      
      if (response.ok) {
        const data = await response.json();
        return data;
      } else {
        return [];
      }
      
    } catch (error) {
      // Backend no disponible, no hay slots bloqueados
      return [];
    }
  }, [businessInfo?.id]);

  /**
   * La ocupación de cada día del mes visible, resuelta por el SERVIDOR con la
   * misma regla que la grilla (`decidirConflicto`).
   *
   * Acá había una estimación propia: se pedía un conteo de turnos y se
   * comparaba contra una capacidad deducida de cuánta gente atiende el
   * servicio. Era otra regla distinta de la de la grilla, y más pobre —no mira
   * duraciones, solapamientos ni recursos—, así que un día podía salir verde y
   * al abrirlo no tener un solo horario libre. Ahora el puntito y la grilla
   * contestan lo mismo porque preguntan lo mismo.
   *
   * Los horarios candidatos de cada día los arma este componente con
   * `generateTimeSlots(fecha, true)` —los del día, ocupados incluidos— porque
   * dependen del horario del negocio, del profesional elegido y de la duración
   * del servicio, que es información que vive acá.
   */
  // Extraído del array de dependencias: una expresión compuesta ahí adentro no
  // se puede verificar estáticamente y el linter no puede avisar si falta algo.
  const nombreDelServicio = services?.[0]?.name;

  useEffect(() => {
    const businessId = businessInfo?.id;
    const servicio = services?.[0];

    // DIAGNÓSTICO TEMPORAL [PUNTO-*] — texto plano a propósito: los objetos
    // salen colapsados como "Object" en la consola y no se pueden pegar.
    if (!businessId || !servicio) {
      console.log(`[PUNTO-1] NO corre la consulta del mes | businessId=${businessId} servicio=${servicio?.name}`);
      return;
    }

    const dias = [];
    const ultimo = new Date(currentYear, currentMonth + 1, 0).getDate();
    const hoy = new Date();
    hoy.setHours(0, 0, 0, 0);

    for (let d = 1; d <= ultimo; d++) {
      const fecha = new Date(currentYear, currentMonth, d);
      // Los días pasados no se pintan, así que no se preguntan.
      if (fecha < hoy) continue;
      const horarios = generateTimeSlots(fecha, true).map((s) => s.time || s);
      if (horarios.length > 0) dias.push({ date: aFechaISO(fecha), horarios });
    }

    if (dias.length === 0) {
      console.log(`[PUNTO-2] 0 días con horarios en ${currentMonth + 1}/${currentYear} — no se pregunta nada`);
      setOcupacionDelMes({});
      return;
    }

    console.log(`[PUNTO-2] pregunto por ${dias.length} días | servicio=${servicio.name} dur=${servicio.duration} staffId=${selectedStaff?.isAnyStaff ? 'CUALQUIERA' : selectedStaff?.id} | ejemplo: ${dias[0].date} con ${dias[0].horarios.length} horarios [${dias[0].horarios.slice(0, 4).join(', ')}...]`);

    let cancelado = false;
    fetch(`${API_URL}/api/public/horarios-ocupados/mes`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        businessId,
        dias,
        serviceName: servicio.name,
        duracionMinutos: servicio.duration,
        staffId: selectedStaff?.isAnyStaff ? null : selectedStaff?.id || null,
        staffName: selectedStaff?.isAnyStaff ? null : selectedStaff?.name || null,
        resourceId: selectedResource?.id || null,
        resourceName: selectedResource?.name || null,
      }),
    })
      .then((r) => {
        console.log(`[PUNTO-3] respuesta HTTP ${r.status}`);
        return r.ok ? r.json() : { ocupacion: {} };
      })
      .then((d) => {
        const oc = d.ocupacion || {};
        const resumen = Object.entries(oc).slice(0, 6)
          .map(([f, v]) => `${f}:${v.ocupados}/${v.total}`).join('  ');
        console.log(`[PUNTO-3] días con datos: ${Object.keys(oc).length} | ${resumen}`);
        if (!cancelado) setOcupacionDelMes(oc);
      })
      // En silencio: sin este dato no hay puntito, pero el calendario sigue
      // sirviendo para reservar. No es motivo para romper la pantalla.
      .catch((e) => {
        console.log(`[PUNTO-3] LA CONSULTA FALLÓ → ${e.message}`);
        if (!cancelado) setOcupacionDelMes({});
      });

    return () => { cancelado = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessInfo?.id, currentMonth, currentYear, refreshTrigger,
      nombreDelServicio, selectedStaff?.id, selectedResource?.id, blockedSlots]);

  // Función para verificar si un horario está bloqueado
  const isSlotBlocked = (fecha, hora) => {
    return blockedSlots.some(blockedSlot => {
      if (blockedSlot.date !== fecha) return false;
      
      // Convertir horas a minutos para comparación
      const [startHour, startMin] = blockedSlot.startTime.split(':').map(Number);
      const [endHour, endMin] = blockedSlot.endTime.split(':').map(Number);
      const [slotHour, slotMin] = hora.split(':').map(Number);
      
      const startTime = startHour * 60 + startMin;
      const endTime = endHour * 60 + endMin;
      const slotTime = slotHour * 60 + slotMin;
      
      // El slot está bloqueado si cae dentro del rango bloqueado
      return slotTime >= startTime && slotTime < endTime;
    });
  };

  // Función optimizada para verificar disponibilidad usando datos del día ya cargados
  const checkSlotAvailability = (fecha, hora, servicioId) => {
    // Primero verificar si el horario está bloqueado
    if (isSlotBlocked(fecha, hora)) {
      return true; // Está bloqueado
    }

    // Verificar disponibilidad considerando staff específico
    const selectedServiceData = services.find(s => s.name === servicioId || s.id === servicioId);
    const staffMode = selectedServiceData?.staffSelectionMode || 'automatic';
    const serviceDuration = selectedServiceData?.duration || 30;
    
    // Acá había un filtro `booking.service?.name === servicioId` cuyo resultado
    // no usaba nadie, y que además nunca podía dar nada: `by-date` no manda el
    // servicio. Quién está ocupado ahora lo contesta el backend (ver
    // `horariosOcupados`); lo que queda de esta función es lo que sí se puede
    // decidir en el navegador: bloqueos y horarios del personal.

    // 🔧 NUEVA LÓGICA: Verificar solapamientos con otros servicios
    const overlappingBookings = dayBookings.filter(booking => {
      if (booking.status === 'cancelled') return false;
      
      const bookingTime = booking.time;
      const bookingService = services.find(s => s.name === booking.service?.name || s.id === booking.service?.id);
      const bookingDuration = bookingService?.duration || 30;
      
      // Convertir tiempos a minutos para comparación
      const [horaHour, horaMin] = hora.split(':').map(Number);
      const [bookingHour, bookingMin] = bookingTime.split(':').map(Number);
      
      const horaStartMinutes = horaHour * 60 + horaMin;
      const horaEndMinutes = horaStartMinutes + serviceDuration;
      const bookingStartMinutes = bookingHour * 60 + bookingMin;
      const bookingEndMinutes = bookingStartMinutes + bookingDuration;
      
      // Verificar si hay solapamiento
      const hasOverlap = horaStartMinutes < bookingEndMinutes && horaEndMinutes > bookingStartMinutes;
      
      return hasOverlap;
    });


    
    // console.log('🔍 CHECK SLOT AVAILABILITY:', {
    //   fecha,
    //   hora,
    //   servicioId,
    //   serviceDuration,
    //   existingBookings: existingBookings.length,
    //   overlappingBookings: overlappingBookings.length,
    //   overlappingDetails: overlappingBookings.map(b => ({
    //     time: b.time,
    //     service: b.service?.name,
    //     duration: services.find(s => s.name === b.service?.name)?.duration || 30
    //   }))
    // });

    // CASO 1: Staff específico seleccionado
    if (staffMode === 'required' && selectedStaff) {
      // Verificar si el staff seleccionado está ocupado en algún momento del servicio
      const staffOccupied = overlappingBookings.some(booking => {
        const bookingStaffId = booking.staff?.id || booking.staff?.name || booking.assignedStaff?.id || booking.assignedStaff?.name;
        const selectedStaffId = selectedStaff?.id || selectedStaff?.name;
        return bookingStaffId === selectedStaffId;
      });
      
      return staffOccupied;
    }

    // CASO 2: Modo "sin preferencia" o "automático"
    if (staffMode === 'optional' || staffMode === 'automatic') {
      // Determinar qué staff está disponible para este horario específico
      const fechaObj = new Date(fecha + 'T00:00:00'); // Forzar timezone local
      const dayName = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'][fechaObj.getDay()];
      const assignedStaff = getAssignedStaff(selectedServiceData);
      
      // Filtrar staff que trabaja en este horario específico
      const staffWorkingAtThisTime = assignedStaff.filter(staff => {
        // Si el staff no tiene horarios configurados, asumir que trabaja en todos los horarios del negocio
        if (!staff.schedule || !staff.schedule[dayName] || !staff.schedule[dayName].enabled) {
          return true; // Staff sin horarios específicos está disponible
        }
        
        const staffRanges = staff.schedule[dayName].ranges || [];
        const horaMinutos = timeToMinutes(hora);
        
        // Verificar si el staff trabaja en este horario
        const worksAtThisTime = staffRanges.some(range => {
          const startMinutes = timeToMinutes(range.start);
          const endMinutes = timeToMinutes(range.end);
          return horaMinutos >= startMinutes && horaMinutos < endMinutes;
        });
        
        return worksAtThisTime;
      });
      
      // Manejar reservas con y sin información de staff.
      // `occupiedStaff` se declara acá y no dentro de cada rama: estaba dos
      // veces como `var` en el if y en el else, que funcionaba solo por el
      // hoisting de var.
      let occupiedStaff;
      if (overlappingBookings.length > 0) {
        const reservasConStaff = overlappingBookings.filter(booking => {
          const staffId = booking.staff?.id || booking.staff?.name || booking.assignedStaff?.id || booking.assignedStaff?.name || booking.staffId || booking.staffName;
          return staffId;
        });
        
        const reservasSinStaff = overlappingBookings.filter(booking => {
          const staffId = booking.staff?.id || booking.staff?.name || booking.assignedStaff?.id || booking.assignedStaff?.name || booking.staffId || booking.staffName;
          return !staffId;
        });
        
        // Calcular staff ocupado por reservas con información
        const occupiedStaffFromKnownBookings = reservasConStaff.map(booking => {
          const staffId = booking.staff?.id || booking.staff?.name || booking.assignedStaff?.id || booking.assignedStaff?.name || booking.staffId || booking.staffName;
          return staffId;
        });
        
        // Calcular staff disponible después de reservas conocidas
        const staffAvailableAfterKnownBookings = staffWorkingAtThisTime.filter(staff => {
          const staffId = staff.id || staff.name;
          return !occupiedStaffFromKnownBookings.includes(staffId);
        });
        
        // Si hay reservas sin staff, verificar si hay suficiente staff disponible
        if (reservasSinStaff.length > 0) {
          // Si las reservas sin staff >= staff disponible, bloquear completamente
          if (reservasSinStaff.length >= staffAvailableAfterKnownBookings.length) {
            return true; // Bloquear completamente
          }
        }
        
        // Continuar con lógica normal usando solo las reservas con staff conocido
        occupiedStaff = occupiedStaffFromKnownBookings;
      } else {
        occupiedStaff = [];
      }
      
      // Verificar si hay staff disponible que trabaje en este horario
      const availableStaffForThisTime = staffWorkingAtThisTime.filter(staff => {
        const staffId = staff.id || staff.name;
        return !occupiedStaff.includes(staffId);
      });
      
      // Solo está ocupado si NO hay staff disponible para este horario específico
      const isOccupied = availableStaffForThisTime.length === 0;
      
      return isOccupied;
    }

    // CASO 3: Fallback - lógica anterior para compatibilidad
    const ocupadoEnBackend = overlappingBookings.length > 0;

    if (ocupadoEnBackend) {
      return true; // Está ocupado según el backend
    }

    // Si no está en el backend, verificar en el contexto local (fallback)
    const localResult = isSlotOcupado(fecha, hora, servicioId);
    return localResult;
  };
  
  // Cargar slots bloqueados al montar el componente
  useEffect(() => {
    const loadBlockedSlots = async () => {
      if (businessInfo?.id) {
        const blocked = await fetchBlockedSlots();
        setBlockedSlots(blocked);
      }
    };

    loadBlockedSlots();
  }, [businessInfo?.id, fetchBlockedSlots]);
  


  useEffect(() => {
    if (services && services.length > 0) {
      // Siempre actualizar al primer servicio cuando cambien los services
      const newService = services[0].name;
      if (selectedService !== newService) {
        setSelectedService(newService);
        // Al cambiar de servicio se limpia el día: los horarios de un servicio
        // no valen para otro (cambia la duración y quién lo atiende), y dejar
        // el día puesto mostraría una lista de horarios recalculada bajo un día
        // que se eligió pensando en otra cosa.
        setSelectedDate(null);
      }
    }
    // `selectedService` queda FUERA de las dependencias a propósito, y esta vez
    // dicho con todas las letras porque la regla de lint pide lo contrario.
    //
    // El efecto existe para elegir el primer servicio cuando cambia la lista.
    // Si `selectedService` estuviera en la lista, elegir el segundo servicio
    // volvería a disparar el efecto: `newService` seguiría siendo el primero,
    // no coincidiría con lo elegido, y lo pisaría. O sea que el usuario no
    // podría cambiar de servicio.
    //
    // La comparación de adentro tampoco es de más: sin ella, `setSelectedDate`
    // pondría una fecha nueva en cada render (es un objeto distinto cada vez) y
    // borraría el día que el usuario venía eligiendo.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [services]);

  // Este efecto ahora SÓLO trae las reservas del día. Antes también generaba
  // los horarios acá adentro, y ese era el bug:
  //
  //   const bookings = await fetchDayBookings(fechaStr);
  //   setDayBookings(bookings);
  //   const slots = generateTimeSlots(selectedDate);  // <- con las reservas VIEJAS
  //
  // `generateTimeSlots` llama a `checkSlotAvailability`, que lee `dayBookings`.
  // Pero `setDayBookings` no cambia la variable de este render: la closure sigue
  // viendo el array anterior, así que los horarios se calculaban contra las
  // reservas del día que se estaba mirando ANTES. Por eso existía un segundo
  // efecto que los volvía a generar cuando `dayBookings` cambiaba: tapaba el
  // problema calculando todo dos veces, y dejaba una ventana en la que la
  // grilla mostraba horarios equivocados.
  useEffect(() => {
    const cargarReservasDelDia = async () => {
      if (!selectedDate || !selectedService || !businessInfo?.id) {
        setDayBookings([]);
        return;
      }

      setLoadingSlots(true);
      try {
        const fechaStr = aFechaISO(selectedDate);
        setDayBookings(await fetchDayBookings(fechaStr));
      } catch (error) {
        setDayBookings([]);
      } finally {
        setLoadingSlots(false);
      }
    };

    cargarReservasDelDia();
  }, [selectedDate, selectedService, businessInfo?.id, refreshTrigger, fetchDayBookings]);


  
  // Generate dates for the current month only
  const getDatesInMonth = () => {
    const dates = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    // Obtener el primer y último día del mes actual
    const firstDay = new Date(currentYear, currentMonth, 1);
    const lastDay = new Date(currentYear, currentMonth + 1, 0);
    
    // Obtener días máximos de anticipación del servicio seleccionado
    const selectedServiceData = services.find(s => s.name === selectedService || s.id === selectedService);
    const maxAdvanceBookingDays = selectedServiceData?.maxAdvanceBookingDays || 30;
    
    // Calcular fecha máxima permitida
    const maxDate = new Date(today);
    maxDate.setDate(maxDate.getDate() + maxAdvanceBookingDays);
    
    // console.log('📅 Validación de fechas:', {
    //   servicio: selectedServiceData?.name,
    //   maxAdvanceBookingDays,
    //   today: today.toISOString().split('T')[0],
    //   maxDate: maxDate.toISOString().split('T')[0]
    // });
    
    // Solo incluir días que no sean anteriores a hoy y no excedan el máximo
    for (let day = 1; day <= lastDay.getDate(); day++) {
      const date = new Date(currentYear, currentMonth, day);
      if (date >= today && date <= maxDate) {
        dates.push(date);
      }
    }
    
    return dates;
  };

  // Funciones para navegar entre meses
  const goToNextPeriod = () => {
    // Verificar si el próximo mes tendría fechas válidas según maxAdvanceBookingDays
    const selectedServiceData = services.find(s => s.name === selectedService || s.id === selectedService);
    const maxAdvanceBookingDays = selectedServiceData?.maxAdvanceBookingDays || 30;
    
    const today = new Date();
    const maxDate = new Date(today);
    maxDate.setDate(maxDate.getDate() + maxAdvanceBookingDays);
    
    const nextMonth = currentMonth === 11 ? 0 : currentMonth + 1;
    const nextYear = currentMonth === 11 ? currentYear + 1 : currentYear;
    const firstDayOfNextMonth = new Date(nextYear, nextMonth, 1);
    
    // Solo permitir avanzar si el primer día del próximo mes está dentro del límite
    if (firstDayOfNextMonth <= maxDate) {
      if (currentMonth === 11) {
        setCurrentMonth(0);
        setCurrentYear(prev => prev + 1);
      } else {
        setCurrentMonth(prev => prev + 1);
      }
    }
  };

  const goToPreviousPeriod = () => {
    const today = new Date();
    const previousMonth = currentMonth === 0 ? 11 : currentMonth - 1;
    const previousYear = currentMonth === 0 ? currentYear - 1 : currentYear;
    
    // No permitir ir a meses anteriores al actual
    if (previousYear < today.getFullYear() || 
        (previousYear === today.getFullYear() && previousMonth < today.getMonth())) {
      return;
    }
    
    setCurrentMonth(previousMonth);
    if (currentMonth === 0) {
      setCurrentYear(prev => prev - 1);
    }
  };

  const goToToday = () => {
    const today = new Date();
    setCurrentMonth(today.getMonth());
    setCurrentYear(today.getFullYear());
    setSelectedDate(new Date());
  };

  // Calcular el mes actual para mostrar en el header
  const getCurrentPeriodInfo = () => {
    return {
      month: currentMonth,
      year: currentYear
    };
  };
  
  const dates = getDatesInMonth();
  const daysOfWeek = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];
  const months = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
  
  // Movida acá arriba porque generateTimeSlots la nombra en sus
  // dependencias, y con `const` no hay hoisting.
  // 🔧 NUEVA FUNCIÓN: Obtener rangos de tiempo disponibles según el tipo de selección
  const getAvailableTimeRanges = (date, dayName, selectedServiceData) => {
    const businessSchedule = businessInfo?.schedule?.[dayName];
    
    if (!businessSchedule || !businessSchedule.enabled || !businessSchedule.ranges) return [];
    
    // Obtener el personal asignado al servicio
    const assignedStaff = getAssignedStaff(selectedServiceData);
    const assignedResources = getAssignedResources(selectedServiceData);
    
    // Determinar el modo de selección
    const staffMode = selectedServiceData?.staffSelectionMode || 'automatic';
    const resourceMode = selectedServiceData?.resourceSelectionMode || 'automatic';
    
    // CASO 1: Personal específico seleccionado
    if (selectedStaff && !selectedStaff.isAnyStaff) {
      // Si se seleccionó un staff específico, usar solo sus horarios
      const staffRanges = getStaffSpecificTimeRanges(selectedStaff, dayName, businessSchedule);
      // Si el staff no tiene horarios configurados, no hay disponibilidad
      if (staffRanges.length === 0) {
        return [];
      }
      return staffRanges;
    }
    
    // CASO 2: Recurso específico seleccionado
    if (resourceMode === 'required' && selectedResource) {
      return getResourceSpecificTimeRanges(selectedResource, dayName, businessSchedule);
    }
    
    // CASO 3: "Sin preferencia" o "Asignación automática"
    if (staffMode === 'optional' || staffMode === 'automatic' || (selectedStaff && selectedStaff.isAnyStaff)) {
      return getCombinedTimeRanges(assignedStaff, assignedResources, dayName, businessSchedule);
    }
    
    // CASO 4: Fallback - Si no hay modo de selección definido, usar horarios combinados del personal
    // Esto evita que se muestren horarios cuando no hay personal disponible
    return getCombinedTimeRanges(assignedStaff, assignedResources, dayName, businessSchedule);
  };

  // Generate time slots based on business hours and staff/resource availability
  const generateTimeSlots = (date, includeOccupied = false) => {
    const slots = [];
    const dayName = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'][date.getDay()];
    const businessSchedule = businessInfo?.schedule?.[dayName];
    
    if (!businessSchedule || !businessSchedule.enabled || !businessSchedule.ranges) return [];
    
    const selectedServiceData = services.find(s => s.name === selectedService || s.id === selectedService);
    const slotDuration = selectedServiceData?.duration || 30;
    const now = new Date();
    const servicioIdToCheck = selectedServiceData?.name;

    // console.log('🔧 GENERATE TIME SLOTS:', {
    //   date: date.toISOString().split('T')[0],
    //   dayName,
    //   serviceName: selectedServiceData?.name,
    //   slotDuration,
    //   businessSchedule
    // });

    // Obtener horarios disponibles según el tipo de selección
    const availableTimeRanges = getAvailableTimeRanges(date, dayName, selectedServiceData);
    
    // console.log('⏰ Available time ranges:', availableTimeRanges);
    
    if (availableTimeRanges.length === 0) return [];

    // Generar slots para cada rango de tiempo disponible
    const processedSlots = new Map(); // Para evitar duplicados
    
    availableTimeRanges.forEach((range, rangeIndex) => {
      // console.log(`📍 Procesando rango ${rangeIndex + 1}:`, range);
      
      const [startHour, startMin] = range.start.split(':').map(Number);
      const [endHour, endMin] = range.end.split(':').map(Number);
    
      let currentTime = new Date(date);
      currentTime.setHours(startHour, startMin, 0, 0);
      
      const rangeEndTime = new Date(date);
      rangeEndTime.setHours(endHour, endMin, 0, 0);
      
      // console.log(`   Rango: ${range.start} - ${range.end} (${slotDuration} min slots)`);
    
      while (currentTime < rangeEndTime) {
        const isPast = currentTime < now;
        
        const slotEndTime = new Date(currentTime);
        slotEndTime.setMinutes(slotEndTime.getMinutes() + slotDuration);
        
        // `aFechaISO` y no `toISOString()`: `currentTime` lleva la hora del slot,
        // y `toISOString` pasa a UTC. En Argentina (UTC-3) eso hace que
        // cualquier turno de las 21:00 en adelante caiga en el día SIGUIENTE, o
        // sea que la disponibilidad de los horarios de la noche se consultaba
        // contra las reservas de otro día: un turno de las 21:30 ya tomado
        // aparecía libre, y uno libre podía aparecer ocupado.
        const fechaStr = aFechaISO(currentTime);
        const horaStr = currentTime.toTimeString().slice(0, 5);
        
        // Excluir slot actual en modo reprogramación
        const isCurrentSlot = isRescheduling && excludeDateTime && 
          excludeDateTime.date === fechaStr && excludeDateTime.time === horaStr;
        
        if (slotEndTime <= rangeEndTime && !isPast && !isCurrentSlot) {
          // Verificar disponibilidad usando los datos ya cargados
          const ocupado = checkSlotAvailability(fechaStr, horaStr, servicioIdToCheck);
          
          // Si includeOccupied es true, agregar todos los slots (disponibles y ocupados)
          // Si es false, solo agregar los disponibles
          if (!ocupado || includeOccupied) {
            // Evitar duplicados por tiempo
            if (!processedSlots.has(horaStr)) {
              const newSlot = {
                time: horaStr,
                available: !ocupado,
                dateObj: new Date(currentTime),
                assignedStaff: range.assignedStaff || null, // Para modo automático
                assignedResource: range.assignedResource || null
              };
              
              processedSlots.set(horaStr, newSlot);
              slots.push(newSlot);
              // console.log(`   ${ocupado ? '❌ Ocupado' : '✅ Disponible'} Slot agregado: ${horaStr}`);
            }
          }
        } else {
          if (isPast) {
            // console.log(`   ⏭️  Slot pasado: ${horaStr}`);
          } else if (isCurrentSlot) {
            // console.log(`   🔄 Slot actual (reprogramación): ${horaStr}`);
          } else {
            // console.log(`   ⏰ Slot fuera de rango: ${horaStr} (fin: ${slotEndTime.toTimeString().slice(0, 5)})`);
          }
        }
        
        // 🔧 MEJORA: Avanzar por la duración del servicio, no por intervalos fijos
        currentTime.setMinutes(currentTime.getMinutes() + slotDuration);
      }
    });
    
    const finalSlots = slots.sort((a, b) => a.time.localeCompare(b.time));
    
    // console.log('🎯 Slots finales generados:', finalSlots.length);
    finalSlots.forEach((slot, i) => {
      // console.log(`   ${i + 1}. ${slot.time} (Staff: ${slot.assignedStaff?.name || 'Auto'})`);
    });
    
    return finalSlots; // Ordenar slots cronológicamente
  };

  const handleSlotClick = (slot, date) => {
    // console.log('🚨🚨🚨 HANDLESLOTCLICK EJECUTADO 🚨🚨🚨');
    // console.log('🖱️ Slot clickeado:', {
    //   time: slot.time,
    //   slotAssignedStaff: slot.assignedStaff?.name || 'Sin asignar'
    // });
    
    // Determinar el personal y recurso a asignar
    // Priorizar selección manual del usuario, luego el asignado al slot
    const finalStaff = selectedStaff || slot.assignedStaff;
    const finalResource = selectedResource || slot.assignedResource;
    
    // console.log('🚨🚨🚨 STAFF FINAL DETERMINADO 🚨🚨🚨');
    // console.log('selectedStaff:', selectedStaff?.name || 'NO HAY');
    // console.log('slot.assignedStaff:', slot.assignedStaff?.name || 'NO HAY');
    // console.log('finalStaff:', finalStaff?.name || 'NO HAY');
    
    const serviceData = services.find(s => s.name === selectedService || s.id === selectedService);
    
    // console.log('📋 BOOKING CALENDAR: Servicio encontrado para slot:', {
    //   serviceName: serviceData?.name,
    //   enableDynamicPricing: serviceData?.enableDynamicPricing,
    //   priceVariationsCount: serviceData?.priceVariations?.length || 0,
    //   priceVariations: serviceData?.priceVariations
    // });
    
    const slotData = {
      date: date,
      time: slot.time,
      service: serviceData,
      staff: finalStaff,
      resource: finalResource,
      businessId: businessInfo?.id,
      wasStaffAutoAssigned: !selectedStaff && slot.assignedStaff, // Indicar si fue asignado automáticamente
      wasResourceAutoAssigned: !selectedResource && slot.assignedResource
    };
    
    // console.log('📤 Enviando datos:', {
    //   staff: slotData.staff?.name || 'Sin asignar',
    //   service: slotData.service?.name || 'Sin servicio'
    // });
    
    onSelectSlot(slotData);
  };

  // Función para verificar si una fecha tiene slots disponibles (sin generar los slots)
  const checkDateHasAvailableSlots = (date) => {
    if (!businessInfo?.schedule || !selectedService) return false;
    
    const dayName = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'][date.getDay()];
    const schedule = businessInfo?.schedule?.[dayName];
    
    // Si el día no está habilitado o no tiene rangos, no hay slots
    if (!schedule || !schedule.enabled || !schedule.ranges || schedule.ranges.length === 0) {
      return false;
    }
    
    // Si hay al menos un rango de horario, potencialmente hay slots disponibles
    return schedule.ranges.some(range => range.start && range.end);
  };

  // 🎨 NUEVA FUNCIÓN: Calcular disponibilidad relativa de un día
  const calculateDateAvailability = (date) => {
    if (!businessInfo?.schedule || !selectedService) return { level: 'none', available: 0, total: 0 };
    
    const selectedServiceData = services.find(s => s.name === selectedService || s.id === selectedService);
    const dayName = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'][date.getDay()];
    
    // Obtener rangos disponibles considerando staff y recursos seleccionados
    const availableTimeRanges = getAvailableTimeRanges(date, dayName, selectedServiceData);
    
    if (availableTimeRanges.length === 0) {
      return { level: 'none', available: 0, total: 0 };
    }
    
    const slotDuration = selectedServiceData?.duration || 30;
    // Acá `date` es un día del calendario (medianoche), así que hoy `toISOString`
    // daba bien por casualidad: a UTC-3 la medianoche local son las 03:00 UTC
    // del mismo día. Va igual por `aFechaISO` para no depender de esa
    // casualidad, que se rompe sola en cualquier zona con offset positivo.
    const fechaStr = aFechaISO(date);
    
    // Calcular slots totales posibles basándonos en los rangos disponibles
    let totalSlots = 0;
    let availableSlots = 0;
    
    availableTimeRanges.forEach(range => {
      if (range.start && range.end) {
        const [startHour, startMin] = range.start.split(':').map(Number);
        const [endHour, endMin] = range.end.split(':').map(Number);
        const rangeMinutes = (endHour * 60 + endMin) - (startHour * 60 + startMin);
        const slotsInRange = Math.floor(rangeMinutes / slotDuration);
        totalSlots += slotsInRange;
        
        // Verificar cuántos slots están bloqueados en este rango
        let availableSlotsInRange = 0;
        for (let minutes = startHour * 60 + startMin; minutes < endHour * 60 + endMin; minutes += slotDuration) {
          const slotTime = minutesToTime(minutes);
          const isBlocked = isSlotBlocked(fechaStr, slotTime);
          if (!isBlocked) {
            availableSlotsInRange++;
          }
        }
        availableSlots += availableSlotsInRange;
      }
    });
    
    // Por ahora, asumir que todos están disponibles si es futuro
    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();
    const isPast = date < now && !isToday;
    
    if (isPast) {
      return { level: 'none', available: 0, total: totalSlots, percentage: 0 };
    }
    
    /**
     * La disponibilidad real de ese día, tal como la contestó el servidor.
     *
     * Reemplaza toda la cuenta que había acá arriba —rangos, slots teóricos,
     * bloqueos— para decidir el color. Esa cuenta seguía viva y era la que
     * pintaba el puntito, y no coincidía con lo que después mostraba la grilla:
     * un día podía salir verde y no tener un solo horario libre.
     *
     * Lo de arriba se conserva porque de ahí sale `totalSlots`, que es lo que
     * distingue "el negocio no abre" de "abre y se llenó". Pero el número que
     * manda es éste.
     */
    const delServidor = ocupacionDelMes[fechaStr];
    if (window.__puntoDebug === fechaStr) {
      console.log(`[PUNTO-4] ${fechaStr} | delServidor=${JSON.stringify(delServidor)} | totalSlots local=${totalSlots}`);
    }
    if (delServidor && delServidor.total > 0) {
      const libres = Math.max(0, delServidor.total - delServidor.ocupados);
      if (libres === 0) {
        return { level: 'lleno', available: 0, total: delServidor.total, percentage: 0 };
      }
      const pct = Math.round((libres / delServidor.total) * 100);
      return {
        level: pct > 50 ? 'high' : 'medium',
        available: libres,
        total: delServidor.total,
        percentage: pct,
      };
    }

    // Sin respuesta del servidor todavía (o día sin horarios): se cae a la
    // cuenta local, que al menos distingue el día cerrado del abierto.
    if (availableSlots === 0 && totalSlots > 0) {
      return { level: 'lleno', available: 0, total: totalSlots, percentage: 0 };
    }

    const availabilityPercentage = totalSlots > 0 ? Math.round((availableSlots / totalSlots) * 100) : 0;

    let level = 'none';
    if (availabilityPercentage > 50) {
      level = 'high';
    } else if (availabilityPercentage > 0) {
      level = 'medium';
    }

    return { 
      level, 
      available: availableSlots, 
      total: totalSlots,
      percentage: availabilityPercentage
    };
  };

  const formatDate = (date) => {
    return new Intl.DateTimeFormat('es-AR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    }).format(date);
  };


  // Obtener personal asignado al servicio
  const getAssignedStaff = (serviceData) => {
    if (!businessInfo?.staff) return [];
    
    const allStaff = businessInfo.staff.filter(staff => staff.active);
    
    // Si el servicio tiene personal asignado específicamente
    if (serviceData?.assignedStaff && serviceData.assignedStaff.length > 0) {
      return allStaff.filter(staff => serviceData.assignedStaff.includes(staff.id));
    }
    
    // Si no, devolver todo el personal activo
    return allStaff;
  };

  // Obtener recursos asignados al servicio
  const getAssignedResources = (serviceData) => {
    if (!businessInfo?.resources) return [];
    
    const allResources = businessInfo.resources.filter(resource => resource.active);
    
    // Si el servicio tiene recursos asignados específicamente
    if (serviceData?.assignedLocation && serviceData.assignedLocation.length > 0) {
      return allResources.filter(resource => serviceData.assignedLocation.includes(resource.id));
    }
    
    // Si no, devolver todos los recursos activos
    return allResources;
  };

  // CASO 1: Horarios para personal específico
  const getStaffSpecificTimeRanges = (staff, dayName, businessSchedule) => {
    if (!staff.schedule) {
      return [];
    }
    
    if (!staff.schedule[dayName]) {
      return [];
    }
    
    if (!staff.schedule[dayName].enabled) {
      return [];
    }
    
    const staffSchedule = staff.schedule[dayName];
    if (!staffSchedule.ranges || staffSchedule.ranges.length === 0) {
      return [];
    }
    
    // Intersectar horarios del personal con horarios del negocio
    return intersectTimeRanges(staffSchedule.ranges, businessSchedule.ranges)
      .map(range => ({
        ...range,
        assignedStaff: staff,
        assignedResource: null
      }));
  };

  // CASO 2: Horarios para recurso específico
  const getResourceSpecificTimeRanges = (resource, dayName, businessSchedule) => {
    if (!resource.schedule || !resource.schedule[dayName] || !resource.schedule[dayName].enabled) {
      return [];
    }
    
    const resourceSchedule = resource.schedule[dayName];
    if (!resourceSchedule.ranges) return [];
    
    // Intersectar horarios del recurso con horarios del negocio
    return intersectTimeRanges(resourceSchedule.ranges, businessSchedule.ranges)
      .map(range => ({
        ...range,
        assignedStaff: null,
        assignedResource: resource
      }));
  };

  // CASO 3: Horarios combinados para "sin preferencia" o "automático"
  const getCombinedTimeRanges = (assignedStaff, assignedResources, dayName, businessSchedule) => {
    // 🔧 CORRECCIÓN: Solo mostrar horarios donde HAY al menos un staff disponible
    const allStaffRanges = [];
    
    // Recopilar todos los rangos de tiempo de cada personal que está disponible este día
    assignedStaff.forEach(staff => {
      if (staff.schedule && staff.schedule[dayName] && staff.schedule[dayName].enabled && staff.schedule[dayName].ranges) {
        const staffRanges = staff.schedule[dayName].ranges;
        
        // Intersectar con horarios del negocio
        const intersectedRanges = intersectTimeRanges(staffRanges, businessSchedule.ranges);
        
        intersectedRanges.forEach(range => {
          allStaffRanges.push({
            ...range,
            staffMember: staff,
            resourceMember: null
          });
        });
      }
    });
    
    // 🚨 CORRECCIÓN CRÍTICA: Si no hay personal disponible este día, NO mostrar horarios
    // No usar horarios del negocio como fallback
    if (allStaffRanges.length === 0) {
      return [];
    }
    
    // 🔧 NUEVA LÓGICA: NO combinar rangos, mantener rangos individuales por staff
    // Esto asegura que solo Juan Demo aparezca disponible en sus días, no María Demo
    
    return allStaffRanges;
  };

  // Función para intersectar rangos de tiempo
  const intersectTimeRanges = (ranges1, ranges2) => {
    const intersections = [];
    
    ranges1.forEach(range1 => {
      ranges2.forEach(range2 => {
        const intersection = getTimeRangeIntersection(range1, range2);
        if (intersection) {
          intersections.push(intersection);
        }
      });
    });
    
    return intersections;
  };

  // Función para obtener la intersección de dos rangos de tiempo
  const getTimeRangeIntersection = (range1, range2) => {
    const start1 = timeToMinutes(range1.start);
    const end1 = timeToMinutes(range1.end);
    const start2 = timeToMinutes(range2.start);
    const end2 = timeToMinutes(range2.end);
    
    const intersectionStart = Math.max(start1, start2);
    const intersectionEnd = Math.min(end1, end2);
    
    if (intersectionStart < intersectionEnd) {
      return {
        start: minutesToTime(intersectionStart),
        end: minutesToTime(intersectionEnd)
      };
    }
    
    return null;
  };

  // Función para combinar rangos solapados y asignar personal
  const combineAndAssignTimeRanges = (timeRanges, availableStaff) => {
    // Solo mostrar logs si hay cambios significativos
    const shouldLog = timeRanges.length > 0;
    
    if (shouldLog) {
      // console.log('🔄 STAFF DISTRIBUTION - Iniciando distribución');
      // console.log('📊 Staff disponible:', availableStaff.map(s => s.name));
    }

    // 🔧 NUEVA LÓGICA: Crear todos los slots únicos y determinar qué staff está disponible en cada uno
    const uniqueSlots = new Set();
    
    // Obtener la duración del servicio actual
    const selectedServiceData = services.find(s => s.name === selectedService || s.id === selectedService);
    const serviceDuration = selectedServiceData?.duration || 30;
    
    
    // Primero, encontrar todos los puntos de tiempo únicos
    
    timeRanges.forEach((range, index) => {
      const startMinutes = timeToMinutes(range.start);
      const endMinutes = timeToMinutes(range.end);
      
      
      // 🔧 MEJORA: Crear slots basados en la duración del servicio, no en intervalos fijos de 1 hora
      for (let minutes = startMinutes; minutes < endMinutes; minutes += serviceDuration) {
        const slotStart = minutesToTime(minutes);
        const slotEnd = minutesToTime(minutes + serviceDuration);
        
        
        // Solo agregar el slot si hay tiempo suficiente para el servicio completo
        if (minutes + serviceDuration <= endMinutes) {
          uniqueSlots.add(`${slotStart}-${slotEnd}`);
        } else {
        }
      }
    });
    
    
    // Ahora, para cada slot único, determinar qué staff está disponible
    const slotStaffMap = new Map();
    
    Array.from(uniqueSlots).forEach(slot => {
      const [slotStart, slotEnd] = slot.split('-');
      const slotStartMinutes = timeToMinutes(slotStart);
      const slotEndMinutes = timeToMinutes(slotEnd);
      
      const availableStaffForSlot = [];
      
      // Verificar qué staff está disponible en este slot específico
      timeRanges.forEach(range => {
        const rangeStartMinutes = timeToMinutes(range.start);
        const rangeEndMinutes = timeToMinutes(range.end);
        
        // Si el rango del staff cubre este slot completamente (considerando la duración del servicio)
        if (rangeStartMinutes <= slotStartMinutes && rangeEndMinutes >= slotEndMinutes) {
          if (range.staffMember && !availableStaffForSlot.some(s => s.name === range.staffMember.name)) {
            availableStaffForSlot.push(range.staffMember);
          }
        }
      });
      
      slotStaffMap.set(slot, {
        start: slotStart,
        end: slotEnd,
        availableStaff: availableStaffForSlot
      });
    });
    
    // Logging para debugging
    if (shouldLog) {
      //   slot,
      //   availableStaff: data.availableStaff.map(s => s.name)
      // })));
    }
    
    // Logging opcional para debugging
    // if (shouldLog) {
    //   // console.log('📊 Reservas existentes del día:', dayBookings.map(b => ({
    //     time: b.time,
    //     staff: b.staff?.name || b.assignedStaff?.name || b.staffName || 'Sin staff'
    //   })));
    // }
    
    // 🔧 NUEVA: Función para seleccionar staff según la lógica correcta
    const selectStaffForSlot = (candidateStaff, timeSlot) => {
      // Filtrar staff que ya está ocupado en esta hora específica
      const availableStaffForThisSlot = candidateStaff.filter(staff => {
        const staffId = staff.id || staff.name;
        
        // Verificar si este staff ya tiene una reserva en esta hora
        const isOccupiedAtThisTime = dayBookings.some(booking => {
          const bookingStaffId = booking.staff?.id || booking.staff?.name || booking.assignedStaff?.id || booking.assignedStaff?.name || booking.staffId || booking.staffName;
          const timeMatch = booking.time === timeSlot;
          return bookingStaffId === staffId && timeMatch;
        });
        
        return !isOccupiedAtThisTime;
      });
      
      if (availableStaffForThisSlot.length === 0) {
        // No hay staff disponible para este slot
        // Log opcional
        // if (shouldLog) {
        //   // console.log(`❌ No hay staff disponible para ${timeSlot}`);
        // }
        return null;
      }
      
      let selectedStaff;
      
      if (availableStaffForThisSlot.length === 1) {
        // CASO 1: Solo hay un staff disponible (horarios NO solapados)
        selectedStaff = availableStaffForThisSlot[0];
        if (shouldLog) {
          // console.log(`👤 Único staff disponible para ${timeSlot}: ${selectedStaff.name}`);
        }
      } else {
        // CASO 2: Múltiple staff disponible (horarios solapados) - SELECCIÓN RANDOM
        const randomIndex = Math.floor(Math.random() * availableStaffForThisSlot.length);
        selectedStaff = availableStaffForThisSlot[randomIndex];
        if (shouldLog) {
          // console.log(`🎲 Staff random seleccionado para ${timeSlot}: ${selectedStaff.name} (de ${availableStaffForThisSlot.length} disponibles)`);
        }
      }
      
      return selectedStaff;
    };
    
    // Convertir a array y asignar personal según la nueva lógica
    const finalResult = Array.from(slotStaffMap.values()).map(range => {
      const timeSlot = `${range.start}-${range.end}`;
      let assignedStaff = null;
      
      if (range.availableStaff.length > 0) {
        // Usar staff específico disponible para este rango de tiempo
        assignedStaff = selectStaffForSlot(range.availableStaff, range.start);
        // Log opcional
        // if (assignedStaff && shouldLog) {
        //   // console.log(`✅ Asignando ${assignedStaff.name} al slot ${timeSlot}`);
        // }
      } else if (availableStaff.length > 0) {
        // Si no hay personal específico para este rango, usar todo el staff disponible
        assignedStaff = selectStaffForSlot(availableStaff, range.start);
        // Log opcional
        // if (assignedStaff && shouldLog) {
        //   // console.log(`🎲 Asignando ${assignedStaff.name} al slot ${timeSlot} (staff general)`);
        // }
      } 
      // Log opcional
      // else if (shouldLog) {
      //   // console.log(`❌ No hay personal disponible para el slot ${timeSlot}`);
      // }
      
      return {
        start: range.start,
        end: range.end,
        assignedStaff,
        assignedResource: null
      };
    });

    // Log para debugging
    if (shouldLog) {
      //   slot: `${r.start}-${r.end}`,
      //   staff: r.assignedStaff?.name || 'Sin asignar'
      // })));
    }

    return finalResult;
  };

  // Funciones auxiliares para conversión de tiempo
  const timeToMinutes = (timeStr) => {
    const [hours, minutes] = timeStr.split(':').map(Number);
    return hours * 60 + minutes;
  };

  const minutesToTime = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
  };

  const { month, year } = getCurrentPeriodInfo();
  
  // Debug function to measure calendar widths
  useEffect(() => {
    const debugCalendarWidths = () => {
      const calendarDays = document.querySelectorAll('.calendar-day');
      const calendarHeaders = document.querySelectorAll('.calendar-day-header');
      
      // console.log('=== CALENDAR DEBUG ===');
      const grid = document.querySelector('.calendar-grid');
      if (grid) {
        // console.log('Calendar Grid Width:', grid.offsetWidth);
        // console.log('Calendar Grid Computed Width:', window.getComputedStyle(grid).width);
        // console.log('Calendar Grid Max Width:', window.getComputedStyle(grid).maxWidth);
      }
      
      const calendarView = document.querySelector('.booking-calendar-view');
      if (calendarView) {
        // console.log('Calendar View Width:', calendarView.offsetWidth);
      }
      
      // console.log('\n=== DAY HEADERS ===');
      calendarHeaders.forEach((header, index) => {
        const width = header.offsetWidth;
        // console.log(`Header ${index + 1}: ${width}px`);
        header.setAttribute('data-width', `${width}px`);
      });
      
      // console.log('\n=== CALENDAR DAYS ===');
      calendarDays.forEach((day, index) => {
        const width = day.offsetWidth;
        // console.log(`Day ${index + 1}: ${width}px`);
        day.setAttribute('data-width', `${width}px`);
      });
      
      // console.log('\n=== GRID COMPUTED STYLES ===');
      if (grid) {
        const computedStyle = window.getComputedStyle(grid);
        // console.log('Grid Template Columns:', computedStyle.gridTemplateColumns);
        // console.log('Grid Gap:', computedStyle.gap);
        // console.log('Grid Width:', computedStyle.width);
        // console.log('Grid Max Width:', computedStyle.maxWidth);
        // console.log('Grid Box Sizing:', computedStyle.boxSizing);
      }
    };

    // Run debug after component mounts
    setTimeout(debugCalendarWidths, 100);
    
    // Also run on window resize
    window.addEventListener('resize', debugCalendarWidths);
    
    return () => {
      window.removeEventListener('resize', debugCalendarWidths);
    };
  }, [selectedDate]); // Re-run when date changes

  // ESTE useMemo VA AL FINAL DEL CUERPO, y no arriba con los efectos, a
  // propósito.
  //
  // `generateTimeSlots` llama a `getAvailableTimeRanges`, que llama a
  // `getAssignedStaff` y a cuatro ayudantes más, todos declarados con `const`
  // más abajo. Un `useMemo` corre DURANTE el render, así que si estuviera
  // arriba esas constantes todavía estarían en la zona muerta temporal y el
  // componente explotaría con "Cannot access 'getAssignedStaff' before
  // initialization".
  //
  // Antes esto no pasaba porque los horarios se generaban dentro de un efecto,
  // que corre DESPUÉS de que el cuerpo entero terminó. Al pasarlo a memo, el
  // orden de declaración dejó de ser indistinto.

  // Los horarios son una derivación de los datos, no un estado aparte: se
  // recalculan solos cuando cambia cualquiera de sus entradas y no pueden
  // quedar desincronizados de las reservas. Antes eran `useState` +
  // `setTimeSlots` desde dos efectos distintos, que es lo que permitía que
  // mostraran una cosa distinta de la que decían los datos.
  /**
   * Los horarios que la grilla del día podría ofrecer, antes de preguntarle al
   * servidor quién está ocupado.
   *
   * Va con `includeOccupied = true`: acá interesa la grilla completa —qué
   * horarios existen según el horario de atención, la duración del servicio y
   * los bloqueos—, y quién está tomado lo decide el backend en el paso
   * siguiente. Separarlo en dos memos es lo que evita la circularidad: si este
   * dependiera de `horariosOcupados` y la consulta dependiera de él, se
   * llamarían para siempre.
   */
  const horariosCandidatos = useMemo(() => {
    if (!selectedDate || !selectedService || !businessInfo?.id) return [];
    return generateTimeSlots(selectedDate, true);
    // Las dependencias son las ENTRADAS de `generateTimeSlots`, no la función.
    // La función se rearma en cada render y arrastra una cadena de cinco
    // ayudantes más; ponerla en la lista haría que el memo se recalcule
    // siempre, que es lo mismo que no memoizar. Lo que importa es que estén
    // todos los datos que lee: si cambia cualquiera, los horarios se rehacen.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedDate,
    selectedService,
    businessInfo?.id,
    businessInfo?.schedule,
    services,
    dayBookings,
    blockedSlots,
    selectedStaff,
    selectedResource,
    isRescheduling,
    excludeDateTime,
  ]);

  /**
   * El servicio elegido, como OBJETO.
   *
   * `selectedService` (el estado de este componente) es un string con el
   * nombre, no el servicio: lo pone `setSelectedService(services[0].name)` unas
   * cuantas líneas más arriba. Leerle `.name` y `.duration` a un string da
   * `undefined` las dos veces, que es exactamente lo que dejaba la consulta de
   * ocupación sin hacerse — el log decía `servicio: null` con el servicio
   * elegido en pantalla.
   *
   * Se busca en `services` por nombre y se cae al primero, que es el que el
   * efecto de arriba eligió.
   */
  const servicioElegido = useMemo(
    () => (services || []).find((s) => s?.name === selectedService) || (services || [])[0] || null,
    [services, selectedService]
  );

  /**
   * QUÉ se está preguntando: día, servicio, profesional, lugar y la grilla.
   *
   * La revalidación NO entra acá, y por eso está separada de `revalidacion`:
   * cambiar de día es una pregunta nueva —hay que esperar la respuesta antes de
   * ofrecer nada—, mientras que revalidar es la misma pregunta de vuelta, y ahí
   * lo que ya está en pantalla sigue siendo lo mejor que tenemos. Si las dos
   * cosas compartieran una sola clave, la grilla parpadearía en blanco cada 45
   * segundos.
   *
   * Se arma como texto para que el efecto no se dispare cuando React rearma los
   * mismos objetos con otra identidad.
   */
  const claveDeSeleccion = useMemo(
    () =>
      JSON.stringify({
        f: selectedDate ? aFechaISO(selectedDate) : null,
        s: servicioElegido?.name || null,
        d: servicioElegido?.duration || null,
        p: selectedStaff?.id || selectedStaff?.name || null,
        l: selectedResource?.id || selectedResource?.name || null,
        h: horariosCandidatos.map((s) => s.time),
        r: refreshTrigger,
      }),
    [selectedDate, servicioElegido, selectedStaff, selectedResource, horariosCandidatos, refreshTrigger]
  );

  /** La última selección que ya tiene respuesta del servidor. */
  const [claveRespondida, setClaveRespondida] = useState(null);

  /**
   * Volver a preguntar quién está ocupado sin que la persona tenga que recargar.
   *
   * Dos disparadores, y ninguno es un `setInterval` a secas:
   *
   *   - volver a la pestaña. Es el caso más común: se deja la reserva a medias,
   *     se atiende otra cosa y se vuelve diez minutos después a una lista de
   *     horarios de hace diez minutos;
   *   - cada 45 segundos, pero SÓLO mientras la pestaña está visible y hay
   *     horarios en pantalla. Un intervalo que corre en una pestaña de fondo, o
   *     antes de que se elija el día, son pedidos al servidor que no le sirven a
   *     nadie.
   *
   * No toca `window.location.reload()`: recargar la página entera borraría el
   * formulario a medio llenar, que es exactamente el trabajo que esto viene a
   * proteger.
   */
  useEffect(() => {
    if (horariosCandidatos.length === 0) return undefined;

    const revalidar = () => setRevalidacion((n) => n + 1);

    const alVolver = () => {
      if (document.visibilityState === 'visible') revalidar();
    };

    const reloj = setInterval(() => {
      if (document.visibilityState === 'visible') revalidar();
    }, 45000);

    document.addEventListener('visibilitychange', alVolver);
    window.addEventListener('focus', alVolver);

    return () => {
      clearInterval(reloj);
      document.removeEventListener('visibilitychange', alVolver);
      window.removeEventListener('focus', alVolver);
    };
  }, [horariosCandidatos.length]);

  useEffect(() => {
    const { f, s, d, h } = JSON.parse(claveDeSeleccion);

    if (!businessInfo?.id || !f || !s || h.length === 0) {
      /* DIAGNÓSTICO — [DIAG-2] La consulta NO se hace. Si aparece esto, el
         problema es anterior: falta el id del negocio, la fecha, el servicio o
         la grilla salió vacía. Temporal. */
      console.log('[DIAG-2] NO se consulta ocupación →', {
        negocioId: businessInfo?.id ?? '(SIN ID)',
        fecha: f,
        servicio: s,
        horariosCandidatos: h.length,
      });
      setHorariosOcupados(new Set());
      return undefined;
    }

    // Si la respuesta llega tarde y ya se cambió de día o de profesional, se
    // descarta: pintar la ocupación de otra consulta es peor que no pintar
    // ninguna.
    let vigente = true;
    // Sólo se vacía la grilla cuando la pregunta es NUEVA. Revalidando, lo que
    // hay en pantalla sigue en pantalla hasta que llegue la respuesta.
    const esPreguntaNueva = claveRespondida !== claveDeSeleccion;
    if (esPreguntaNueva) setConsultandoOcupados(true);

    (async () => {
      const cuerpo = {
        businessId: businessInfo.id,
        date: f,
        horarios: h,
        serviceName: s,
        duracionMinutos: d,
        staffId: selectedStaff?.id || null,
        staffName: selectedStaff?.name || null,
        resourceId: selectedResource?.id || null,
        resourceName: selectedResource?.name || null,
        // Al reprogramar, el propio turno no puede ser su impedimento.
        ignorarReservaId: isRescheduling ? currentBooking?.id || null : null,
      };

      /* DIAGNÓSTICO — [DIAG-3] Lo que se pregunta y lo que contesta el servidor.
         Si `pedido.staffId`/`staffName` vienen en null, el servidor no puede
         saber por quién preguntar y va a contestar que no hay nada ocupado.
         Temporal. */
      console.log('[DIAG-3] pregunto ocupación →', {
        fecha: cuerpo.date,
        servicio: cuerpo.serviceName,
        duracion: cuerpo.duracionMinutos,
        staffId: cuerpo.staffId,
        staffName: cuerpo.staffName,
        candidatos: h.length,
        primeros: h.slice(0, 6),
      });

      try {
        const respuesta = await fetch(`${API_URL}/api/public/horarios-ocupados`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(cuerpo),
        });

        if (!respuesta.ok) throw new Error(`HTTP ${respuesta.status}`);
        const datos = await respuesta.json();
        console.log('[DIAG-3] respuesta ←', {
          ocupados: datos.ocupados,
          cuantos: (datos.ocupados || []).length,
        });
        if (vigente) {
          setHorariosOcupados(new Set(datos.ocupados || []));
          setClaveRespondida(claveDeSeleccion);
        }
      } catch (error) {
        console.log('[DIAG-3] la consulta FALLÓ →', error.message);
        // Si la consulta falla no se inventa disponibilidad: se dejan los
        // horarios como estaban y el backend sigue siendo el que rechaza al
        // crear. Ofrecer todo como libre sería volver al bug.
        console.warn('No se pudieron consultar los horarios ocupados:', error);
      } finally {
        if (vigente) setConsultandoOcupados(false);
      }
    })();

    return () => {
      vigente = false;
    };
    // `revalidacion` está en la lista aunque no se lea en el cuerpo: es el que
    // hace que la misma pregunta se vuelva a hacer.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [claveDeSeleccion, revalidacion, businessInfo?.id]);

  /**
   * Lo que se muestra: la grilla del día menos lo que el servidor marcó tomado.
   *
   * Mientras la consulta viaja no se ofrece nada. Es a propósito: mostrar los
   * horarios y sacarlos medio segundo después es lo que deja clickear el que ya
   * estaba ocupado.
   */
  const timeSlots = useMemo(() => {
    if (consultandoOcupados) return [];
    const finales = horariosCandidatos
      .filter((slot) => slot.available && !horariosOcupados.has(slot.time));

    /* DIAGNÓSTICO — [DIAG-4] Qué se termina mostrando. Si un horario aparece en
       `ocupadosSegunServidor` y también en `seMuestran`, el filtro no está
       agarrando (por ejemplo, formato de hora distinto: "17:00" vs "17:0").
       Temporal. */
    console.log('[DIAG-4] grilla →', {
      candidatos: horariosCandidatos.length,
      ocupadosSegunServidor: [...horariosOcupados],
      seMuestran: finales.map((s) => s.time),
    });

    return finales;
  }, [horariosCandidatos, horariosOcupados, consultandoOcupados]);


  // El bloque de horarios, para que quien nos monta pueda llevar la pantalla
  // hasta acá cuando se elige un día.
  const horariosRef = useRef(null);

  // El último día que ya avisamos. Sin esto el aviso sale de nuevo cada vez que
  // los horarios se recargan sin que el día haya cambiado —cambiar de
  // profesional o el `refreshTrigger` los vuelven a pedir—, y la pantalla se
  // movería sola mientras la persona está mirando la lista.
  const diaAvisado = useRef(null);

  useEffect(() => {
    if (!alMostrarHorarios || !selectedDate) return;

    // Recién cuando terminaron de cargar: mientras `loadingSlots` está en true
    // el bloque dice "Cargando horarios..." y mide unas pocas líneas, así que
    // llevar la pantalla ahí la deja apuntando a un lugar que en un momento se
    // va a estirar y correr solo.
    if (loadingSlots) return;

    const dia = aFechaISO(selectedDate);
    if (diaAvisado.current === dia) return;
    diaAvisado.current = dia;

    alMostrarHorarios(horariosRef.current);
    // `alMostrarHorarios` no está en las dependencias a propósito: llega como
    // función nueva en cada render del padre y volvería a correr esto siempre.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate, loadingSlots]);

  return (
    <div className="booking-calendar">
      {/* Calendar Section */}
      <div className="calendar-section">
        {/* Service Selection */}
       {/*  <div className="service-selection">
          <h3>Elegí el servicio</h3>
          <div className="service-buttons">
            {services.map((service, index) => (
              <button
                key={service.id || service.name || index}
                className={`service-btn ${selectedService === (service.id || service.name) ? 'active' : ''}`}
                onClick={() => setSelectedService(service.id || service.name)}
              >
                <span className="service-name">{service.name}</span>
                <span className="service-details">
                  {service.duration} min • ${service.price}
                </span>
              </button>
            ))}
          </div>
         </div> */}

        {/* Calendar View */}
        <div className="booking-calendar-view">
          <div className="calendar-header">
            <div className="calendar-navigation">
              <button 
                onClick={goToPreviousPeriod} 
                disabled={currentMonth === new Date().getMonth() && currentYear === new Date().getFullYear()}
                className="nav-btn prev"
                title="Mes anterior"
              >
                ‹
              </button>
              
              <div className="period-info">
                <h3>
                  {`${months[currentMonth]} ${currentYear}`}
                </h3>
                {(currentMonth !== new Date().getMonth() || currentYear !== new Date().getFullYear()) && (
                  <button onClick={goToToday} className="today-btn">
                    Volver a hoy
                  </button>
                )}
              </div>
              
              <button 
                onClick={goToNextPeriod} 
                className="nav-btn next"
                title="Mes siguiente"
              >
                ›
              </button>
            </div>
          </div>
          <div className="calendar-grid">
            {daysOfWeek.map(day => (
              <div key={day} className="calendar-day-header">
                {day.slice(0, 3)}
              </div>
            ))}
            
            {/* Add spacer divs for alignment */}
            {Array.from({ length: dates[0].getDay() }).map((_, index) => (
              <div key={`spacer-${index}`} className="calendar-day empty"></div>
            ))}

            {dates.map((date, index) => {
              const dayOfWeek = date.getDay();
              const today = new Date();
              today.setHours(0, 0, 0, 0);
              const isToday = date.getTime() === today.getTime();
              // `selectedDate` puede ser null —arranca sin nada elegido—, así
              // que se pregunta antes de pedirle el día.
              const isSelected =
                Boolean(selectedDate) && date.toDateString() === selectedDate.toDateString();
              
              // 🔄 NUEVO: Marcar día del turno actual en reprogramación
              const isCurrentBookingDate = isRescheduling && currentBooking && 
                aFechaISO(date) === currentBooking.date;
              
              // Siempre calcular la disponibilidad real de cada día
              // No usar timeSlots del estado porque eso afecta a todos los días
              const availability = calculateDateAvailability(date);
              
              /* `none` es "el negocio no abre ese día": ni puntito ni clic.
                 `lleno` sí muestra puntito —rojo— porque el día existe y la
                 información es justamente que se agotó: sin puntito, un día
                 lleno se ve igual que uno cerrado. */
              const hasAvailableSlots = availability.level !== 'none';
              const mostrarPunto = availability.level !== 'none';
              
              return (
                <div
                  key={index}
                  className={`calendar-day ${isToday ? 'today' : ''} ${isSelected ? 'selected' : ''} ${!hasAvailableSlots ? 'no-slots' : ''} ${isCurrentBookingDate ? 'current-booking-date' : ''}`}
                  onClick={() => setSelectedDate(date)}
                >
                  <span className="day-number">{date.getDate()}</span>
                  {mostrarPunto && (
                    <span
                      className={`availability-dot ${availability.level}`}
                      title={
                        availability.level === 'lleno'
                          ? 'Sin turnos disponibles'
                          : `${availability.available} de ${availability.total} turnos disponibles`
                      }
                    ></span>
                  )}
                  {isCurrentBookingDate && (
                    <span className="current-booking-indicator">📅</span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Time Slots Section */}
      <div className="time-slots-section">
        <div ref={horariosRef} className={`time-slots ${selectedDate ? 'visible' : ''}`}>
          <h3>Horarios disponibles</h3>
          <div className="slots-grid">
            {loadingSlots ? (
              <div className="loading-slots">
                <p>Cargando horarios...</p>
              </div>
            ) : timeSlots.length > 0 ? (
              <>
                {/* 🔄 NUEVO: Mostrar horario actual del turno si estamos en esa fecha */}
                {isRescheduling && currentBooking && selectedDate &&
                 aFechaISO(selectedDate) === currentBooking.date && (
                  <div className="current-booking-slot">
                    <div className="current-slot-header">
                      <span>📅 Tu turno actual:</span>
                    </div>
                    <button className="time-slot current-booking-time" disabled>
                      {currentBooking.time} hs ✓
                    </button>
                  </div>
                )}
                
                {/* Time slots disponibles */}
                {timeSlots.map((slot, index) => (
                  <button
                    key={index}
                    className="time-slot available"
                    onClick={() => handleSlotClick(slot, selectedDate)}
                  >
                    {slot.time}
                  </button>
                ))}
              </>
            ) : (
              <div className="no-slots-message">
                <p>No hay horarios disponibles para este día</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookingCalendar; 